import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api.js';
import type { CourseLayout, PlayerRow } from '../api.js';
import { Board } from '../components/Board.js';
import { useSession } from '../App.js';

/**
 * The waiting room for one game: who has joined, which docking bay they
 * take, and the control to begin. Seats are the real ordering the engine
 * uses, so they are shown as the bay numbers they are.
 *
 * And the course itself, because deciding whether to sit down at a game
 * means looking at the board it is played on. The name told a player
 * nothing: courses are built per game, so "Docking bay" is a name the
 * host typed and not a course anybody has seen before.
 */

/**
 * Cell size for the preview, smaller than the course builder's 50. The
 * live game draws at 44, where robots have to be told apart.
 */
const PREVIEW_PX = 33;
export function GameLobby() {
  const { gameId } = useParams();
  const id = Number(gameId);
  const navigate = useNavigate();
  const { me } = useSession();

  const [players, setPlayers] = useState<PlayerRow[] | null>(null);
  const [courseName, setCourseName] = useState<string | null>(null);
  const [layout, setLayout] = useState<CourseLayout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await api.players(id);
      setPlayers(result.players);
      setCourseName(result.courseName);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    }
  }, [id]);

  // Fetched ONCE, outside the three-second poll the roster runs on. A
  // course is built with the game and does not change, so re-reading it
  // every three seconds would be a composed grid an hour for a page
  // somebody has left open. A failure is left quiet: no preview is a
  // smaller thing than an error where the board should be.
  useEffect(() => {
    void (async () => {
      try {
        setLayout(await api.courseLayout(id));
      } catch {
        setLayout(null);
      }
    })();
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const timer = setInterval(() => { void load(); }, 3000);
    return () => clearInterval(timer);
  }, [load]);

  const status = players?.[0]?.status;
  const createdBy = players?.[0]?.created_by ?? null;
  const isHost = me !== null && createdBy === me.userId;
  const joined = players?.some((p) => p.user_id === me?.userId) ?? false;
  // The host's setting, off the same roster rows the status comes from.
  // A game nobody has joined has no rows at all, and there is nothing to
  // join in progress either, so the fallback never decides anything.
  const lateJoin = (players?.[0]?.late_join ?? 1) !== 0;
  const canJoin = !joined && (status === 'lobby' || (status === 'active' && lateJoin));

  const join = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.joinGame(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not join.');
    } finally {
      setBusy(false);
    }
  };

  const begin = async () => {
    // A game of one is legal and is sometimes what the host wants -- a
    // course walked through alone to see how it plays. It is far more
    // often the host starting before anybody else has arrived, and that
    // cannot be undone: joining is refused once a game is under way
    // unless late joiners were left open, and even then the latecomer
    // comes in level with whoever is last rather than at the start.
    //
    // Not asked where the host set one seat: that is a solo game on
    // purpose, and nobody else could have joined anyway.
    if ((players?.length ?? 0) === 1 && players?.[0]?.max_players !== 1 && !window.confirm(
      'Start with only one player? Nobody else has taken a seat yet.',
    )) return;
    setBusy(true);
    setError(null);
    try {
      await api.startGame(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the game.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Ends the game without a winner.
   *
   * Distinct from deleting it: the game stops being live but its log, its
   * course and its roster all survive. Nothing goes to the rankings --
   * there is no winner to rank.
   */
  const finish = async () => {
    if (!window.confirm(
      'Finish this game with no winner? It will not be added to the rankings.',
    )) return;
    setBusy(true);
    setError(null);
    try {
      await api.finishGame(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not finish the game.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this game? It cannot be undone.')) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteGame(id);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the game.');
      setBusy(false);
    }
  };

  // `wide`, like the course editor: half the gutter of a plain `.content`,
  // because the course preview below is as wide as the course itself and
  // three boards across did not fit the reading measure. The margins were
  // only buying a scrollbar.
  return (
    <main className="content wide stack" style={{ gap: '2rem' }}>
      <div className="spread">
        <div>
          <p className="eyebrow">Game {id}</p>
          {/* The name the host gave it. This said "Docking bay" for every
              game ever opened -- the heading of the screen rather than
              the name of the thing on it -- so a host who named a game
              found the name nowhere but the lobby list. */}
          <h2>{courseName ?? 'Untitled course'}</h2>
        </div>
        <button className="secondary" onClick={() => navigate('/')}>Back to lobby</button>
      </div>

      {error && <p className="notice">{error}</p>}

      {/* The course, at the size it was built at. No robots and no
          tokens: nothing has been placed yet, and on a started game this
          is the waiting room rather than the board -- the live positions
          are through "Go to the board", which is the page that has the
          log and the controls to read them against. */}
      {layout && (
        <section className="stack" style={{ gap: '0.5rem' }}>
          <p className="eyebrow">The course</p>
          <div style={{ overflowX: 'auto' }}>
            <Board
              layout={layout}
              robots={[]}
              colours={new Map()}
              youAre={null}
              nameOf={(robotId) => robotId}
              tokens={[]}
              cellPx={PREVIEW_PX}
            />
          </div>
        </section>
      )}

      {/* Without this a finished game looks like a broken one: every
          action but Delete disappears and nothing says why. */}
      {status === 'finished' && (
        <p className="notice ok">
          This game has finished. Its log and course are kept; the game
          itself is over.
        </p>
      )}

      {/* What joining a game already under way costs, said before the
          button rather than after it. Where a latecomer starts is the
          whole of the deal and none of it is guessable from the board. */}
      {!joined && status === 'active' && (
        <p className="notice">
          This game is under way. You would come in at the start of the
          next turn, on the last flag every player has tagged, or on the
          start track if anyone has yet to reach flag 1, and play from
          there like anybody else.
        </p>
      )}

      {players === null && <p className="eyebrow">Loading</p>}

      {players?.length === 0 && (
        <div className="empty"><p className="muted">Nobody has taken a bay yet.</p></div>
      )}

      {/* Who is in, and which one is you. That is the whole of what this
          list is for.

          No bay number. Start positions are dealt out at random rather
          than seat N taking start N, so the seat is an internal ordering
          and naming it told a player nothing they could act on. It used
          to be said twice: a stamped plate and a "bay N" line under the
          name.

          And no longer `game-row`, which is built for the lobby's
          three-line game cards -- a row carrying one short name stood as
          tall as one carrying a course, a turn count, a host and a
          roster, and ran the width of the page to do it. */}
      <div className="stack" style={{ gap: '0.5rem' }}>
        {players?.map((player) => (
          <div key={player.robot_id} className="seat-row">
            <span className="display">{player.display_name}</span>
            {player.user_id === me?.userId && <span className="tag lobby">You</span>}
          </div>
        ))}
      </div>

      {/* Said while the table is still filling, because a lobby with one
          name on it and a live Start button reads as a game waiting on
          the host rather than one waiting on players. */}
      {status === 'lobby' && (players?.length ?? 0) > 0 && (
        <p className="muted">Awaiting additional players.</p>
      )}

      <div className="row">
        {/* Two different things to be doing: taking a seat before the
            game starts, and coming in partway through one already under
            way. The label says which. */}
        {canJoin && (
          <button onClick={() => void join()} disabled={busy}>
            {status === 'lobby' ? 'Join' : 'Join game in progress'}
          </button>
        )}
        {/* Said rather than left blank: a player arriving at a game
            already under way needs to know the door is shut, not wonder
            where the button went. */}
        {!joined && status === 'active' && !lateJoin && (
          <p className="muted">This game was set up closed to new players.</p>
        )}
        {isHost && status === 'lobby' && (
          <button onClick={() => void begin()} disabled={busy || (players?.length ?? 0) === 0}>
            Start
          </button>
        )}
        {status === 'active' && (
          <button onClick={() => navigate(`/games/${id}/play`)}>
            Go to the board
          </button>
        )}
        {/* Between playing to the end and destroying it. A table that
            breaks up halfway wants neither: the game should stop being
            live without the log and the course going with it. Hidden once
            the game is already finished, since there is nothing to end. */}
        {isHost && status !== 'finished' && (
          <button className="secondary" onClick={() => void finish()} disabled={busy}>
            Finish
          </button>
        )}
        {/* An administrator may delete anyone's game. A game whose host
            has gone could otherwise be deleted by nobody and would sit in
            the lobby for good. The server decides either way; showing the
            button only saves an admin guessing that it would work. */}
        {(isHost || me?.isAdmin) && (
          <button className="danger" onClick={() => void remove()} disabled={busy}>
            Delete
          </button>
        )}
      </div>
    </main>
  );
}
